import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { User, Session } from '@supabase/supabase-js'
import { useLocation } from 'wouter'

export type UserRole = 'admin' | 'user'

export interface AuthUser {
  id: string
  email: string
  firstName?: string
  lastName?: string
  username?: string
  role: UserRole
  profileImageUrl?: string
  referralCode?: string
  idVerified?: boolean
  idVerifiedAt?: Date | null
  phoneVerified?: boolean
  phoneVerifiedAt?: Date | null
  phone?: string | null
  createdAt?: Date | string | null
  profileCompletion?: number
  supabaseUser: User
}

interface AuthContextType {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  signUp: (email: string, password: string, metadata?: { firstName?: string; lastName?: string }) => Promise<{ error: any }>
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signInWithMagicLink: (email: string) => Promise<{ error: any }>
  signInWithGoogle: () => Promise<{ error: any }>
  signInWithFacebook: () => Promise<{ error: any }>
  signOut: () => Promise<void>
  redirectToDashboard: () => void
  resetPassword: (email: string) => Promise<{ error: any }>
  updatePassword: (newPassword: string) => Promise<{ error: any }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [, navigate] = useLocation()

  useEffect(() => {
    // Check if Supabase is configured
    if (!supabase) {
      console.warn('Supabase not configured, using demo mode')
      // In demo mode, create a demo user
      const demoUser: AuthUser = {
        id: 'demo-user',
        email: 'demo@prizedrop.com',
        firstName: 'Demo',
        lastName: 'User',
        role: 'user',
        profileImageUrl: undefined,
        referralCode: 'DEMO123',
        supabaseUser: null as any
      }
      setUser(demoUser)
      setLoading(false)
      return
    }

    // Get initial session
    const getInitialSession = async () => {
      const { data: { session: initialSession } } = await supabase!.auth.getSession()

      if (initialSession) {
        setSession(initialSession)
        await fetchUserData(initialSession.user, initialSession)
      } else {
        setLoading(false)
      }
      setInitialLoad(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase!.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, !!session, {
          hasUser: !!session?.user,
          email: session?.user?.email,
          currentPath: window.location.pathname
        })

        if (event === 'SIGNED_OUT') {
          setUser(null)
          setSession(null)
          setLoading(false)
          setInitialLoad(false)
          // Clear any stored deactivation error messages
          sessionStorage.removeItem('authErrorMessage')
          return
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log(`[AUTH STATE] ${event} event triggered`, {
            userId: session?.user?.id,
            email: session?.user?.email,
            currentPath: window.location.pathname,
            provider: session?.user?.app_metadata?.provider,
            hasProviderToken: !!session?.provider_token
          })

          // Enhanced OAuth detection
          if (event === 'SIGNED_IN' && session?.user?.app_metadata?.provider === 'google') {
            console.log('[AUTH STATE] Google OAuth sign-in detected, processing authentication...')
          }

          setSession(session)
          if (session?.user) {
            if (event === 'SIGNED_IN') {
              await fetchUserData(session.user, session)
            } else {
              // TOKEN_REFRESHED - update user data silently without redirect logic
              await fetchUserDataSilently(session.user, session)
            }
          } else {
            setLoading(false)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const fetchUserDataSilently = async (supabaseUser: User, userSession: Session) => {
    try {
      const response = await fetch('/api/auth/user', {
        headers: {
          'Authorization': `Bearer ${userSession.access_token}`
        }
      })

      if (response.ok) {
        const userData = await response.json()
        const newUser: AuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email!,
          firstName: userData.firstName || supabaseUser.user_metadata?.firstName,
          lastName: userData.lastName || supabaseUser.user_metadata?.lastName,
          username: userData.username,
          role: userData.role || 'user',
          profileImageUrl: userData.profileImageUrl,
          referralCode: userData.referralCode,
          phoneVerified: userData.phoneVerified || false,
          phoneVerifiedAt: userData.phoneVerifiedAt ? new Date(userData.phoneVerifiedAt) : null,
          phone: userData.phone || null,
          supabaseUser
        }

        // Set user state without any redirect logic (silent update)
        setUser(newUser)
      }
    } catch (error) {
      console.error('Error silently fetching user data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserData = async (supabaseUser: User, userSession: Session) => {
    try {
      // Keep loading state minimal - only set during actual data fetch

      const response = await fetch('/api/auth/user', {
        headers: {
          'Authorization': `Bearer ${userSession.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const userData = await response.json()
        const newUser: AuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email!,
          firstName: userData.firstName || supabaseUser.user_metadata?.firstName,
          lastName: userData.lastName || supabaseUser.user_metadata?.lastName,
          username: userData.username,
          role: userData.role || 'user',
          profileImageUrl: userData.profileImageUrl,
          referralCode: userData.referralCode,
          idVerified: userData.idVerified || false,
          idVerifiedAt: userData.idVerifiedAt ? new Date(userData.idVerifiedAt) : null,
          phoneVerified: userData.phoneVerified || false,
          phoneVerifiedAt: userData.phoneVerifiedAt ? new Date(userData.phoneVerifiedAt) : null,
          phone: userData.phone || null,
          createdAt: userData.createdAt ? new Date(userData.createdAt) : null,
          supabaseUser
        }

        // Set user state immediately to prevent race conditions
        setUser(newUser)

        // Handle redirect ONLY when user logs in successfully on auth page
        const currentPath = window.location.pathname
        const isOnAuthPage = currentPath === '/auth'

        if (isOnAuthPage) {
          const urlParams = new URLSearchParams(window.location.search)
          const redirectUrl = urlParams.get('redirect')
          const intent = urlParams.get('intent')
          const challengeId = urlParams.get('challengeId')
          const isMobileRedirect = urlParams.get('mobile') === '1'

          console.log(`[AUTH SUCCESS] User authenticated, redirect=${redirectUrl}, intent=${intent}, challengeId=${challengeId}`)
          console.log(`[DEBUG] Redirect logic conditions - redirectUrl exists: ${!!redirectUrl}, intent===join: ${intent === 'join'}, challengeId exists: ${!!challengeId}`)

          // Check if this is a Google OAuth user with incomplete profile
          const isGoogleUser = supabaseUser.app_metadata?.provider === 'google'
          console.log(`[PROFILE CHECK] Is Google user: ${isGoogleUser}`)

          // For Google users without specific redirects, check profile completion
          if (isGoogleUser && !redirectUrl && intent !== 'join') {
            // Check required profile fields directly from the user data
            const requiredFields = ['firstName', 'lastName', 'phone', 'dateOfBirth', 'gender'];
            const hasAllRequiredFields = requiredFields.every(field => {
              const value = (newUser as any)[field];
              return value && value.toString().trim();
            });

            console.log(`[PROFILE CHECK] Required fields check:`, {
              firstName: !!(newUser as any).firstName,
              lastName: !!(newUser as any).lastName,
              phone: !!(newUser as any).phone,
              dateOfBirth: !!(newUser as any).dateOfBirth,
              gender: !!(newUser as any).gender,
              hasAllRequiredFields
            });

            // Also check profile completion from API as backup
            const profileResponse = await fetch('/api/profile/status', {
              headers: {
                'Authorization': `Bearer ${userSession.access_token}`
              }
            })

            let profileCompletion = 0;
            if (profileResponse.ok) {
              const profileData = await profileResponse.json()
              profileCompletion = profileData?.progress?.profileCompletion || 0
              console.log(`[PROFILE CHECK] API profile completion: ${profileCompletion}%`)
            }

            // Only redirect to profile completion if missing required fields AND low completion
            const isProfileIncomplete = !hasAllRequiredFields && profileCompletion < 50;

            if (isProfileIncomplete) {
              console.log(`[PROFILE CHECK] Redirecting Google user to profile completion - missing required fields`)
              navigate('/progressive-profiling')
              return
            } else {
              console.log(`[PROFILE CHECK] Google user has complete profile, proceeding to dashboard`)
            }
          }

          // Check for competition redirect from facebook-login page
          const compParam = urlParams.get('comp')
          
          // Navigate immediately without timeout to prevent 404 flash
          if (redirectUrl) {
            // Enhance redirect URL with auto-join markers for challenges
            let finalRedirectUrl = redirectUrl
            if (intent === 'join' && challengeId) {
              const redirectUrlObj = new URL(redirectUrl, window.location.origin)
              redirectUrlObj.searchParams.set('from', 'auth')
              redirectUrlObj.searchParams.set('intent', 'join')
              redirectUrlObj.searchParams.set('autoJoin', 'true')
              finalRedirectUrl = redirectUrlObj.pathname + redirectUrlObj.search
            }
            console.log(`[AUTH SUCCESS] Navigating to ${finalRedirectUrl}`)
            navigate(finalRedirectUrl)
          } else if (compParam) {
            // Handle competition redirect from facebook-login page
            const competitionUrl = isMobileRedirect ? `/m/competition/${compParam}` : `/competition/${compParam}`
            console.log(`[AUTH SUCCESS] Navigating to competition from facebook-login: ${competitionUrl}`)
            navigate(competitionUrl)
          } else if (intent === 'join' && challengeId) {
            // Handle challenge join when redirectUrl is null but intent and challengeId are present
            const challengeBase = isMobileRedirect ? `/m/challenges/${challengeId}` : `/challenges/${challengeId}`
            const challengeUrl = `${challengeBase}?from=auth&intent=join&autoJoin=true`
            console.log(`[AUTH SUCCESS] Navigating to challenge with auto-join: ${challengeUrl}`)
            navigate(challengeUrl)
          } else if (newUser.role === 'admin') {
            console.log(`[AUTH SUCCESS] Navigating to admin dashboard`)
            navigate('/admin')
          } else {
            console.log(`[AUTH SUCCESS] Navigating to user dashboard`)
            navigate(isMobileRedirect ? '/m' : '/user')
          }
        }
      } else if (response.status === 404) {
        // User doesn't exist, create them
        // Get referral code and competition ID from URL if present
        const urlParams = new URLSearchParams(window.location.search)
        let referralCode = urlParams.get('ref')
        const redirectUrl = urlParams.get('redirect')

        console.log(`[USER CREATION] Creating user after email verification - email: ${supabaseUser.email}, referralCode: ${referralCode}`)

        console.log(`[SIGNUP DEBUG] Initial extraction - ref: ${referralCode}, redirect: ${redirectUrl}`)

        // Extract competition ID from current URL or redirect URL
        let competitionId = null

        // First check for direct 'comp' parameter in URL
        const compParam = urlParams.get('comp')
        if (compParam) {
          competitionId = parseInt(compParam)
          console.log(`[SIGNUP DEBUG] Found comp parameter: ${competitionId}`)
        }

        // If we have a redirect URL, extract referral code and competition ID from it
        if (redirectUrl) {
          const decodedRedirectUrl = decodeURIComponent(redirectUrl)
          console.log(`[SIGNUP DEBUG] Decoded redirect URL: ${decodedRedirectUrl}`)

          // Extract referral code from redirect URL if not found in direct params
          if (!referralCode && decodedRedirectUrl.includes('ref=')) {
            const redirectParams = new URLSearchParams(decodedRedirectUrl.split('?')[1] || '')
            referralCode = redirectParams.get('ref')
            console.log(`[SIGNUP DEBUG] Extracted referralCode from redirect: ${referralCode}`)
          }

          // Extract competition ID from redirect URL (if not already found in comp parameter)
          if (!competitionId) {
            const match = decodedRedirectUrl.match(/\/competition\/(\d+)/)
            if (match) {
              competitionId = parseInt(match[1])
              console.log(`[SIGNUP DEBUG] Extracted competitionId from redirect: ${competitionId}`)
            }
          }
        } else if (!competitionId) {
          // First check current URL for competition ID (e.g., /competition/64?ref=CODE)
          const currentPath = window.location.pathname
          if (currentPath.includes('/competition/')) {
            const match = currentPath.match(/\/competition\/(\d+)/)
            if (match) {
              competitionId = parseInt(match[1])
              console.log(`[SIGNUP DEBUG] Extracted competitionId from current path: ${competitionId}`)
            }
          }
        }

        // Check for challenge ID (new challenge referral system)
        let challengeId = null
        let fromChallengePage = false

        // Check URL parameters for challenge ID
        const challengeParam = urlParams.get('challenge') || urlParams.get('challengeId')
        if (challengeParam) {
          challengeId = parseInt(challengeParam)
          fromChallengePage = true
          console.log(`[SIGNUP DEBUG] Found challenge parameter: ${challengeId}`)
        }

        // Extract challenge ID from redirect URL
        if (!challengeId && redirectUrl) {
          const decodedRedirectUrl = decodeURIComponent(redirectUrl)
          const challengeMatch = decodedRedirectUrl.match(/\/challenges\/(\d+)/)
          if (challengeMatch) {
            challengeId = parseInt(challengeMatch[1])
            fromChallengePage = true
            console.log(`[SIGNUP DEBUG] Extracted challengeId from redirect: ${challengeId}`)
          }
        }

        // Check current path for challenge ID
        if (!challengeId) {
          const currentPath = window.location.pathname
          if (currentPath.includes('/challenges/')) {
            const challengeMatch = currentPath.match(/\/challenges\/(\d+)/)
            if (challengeMatch) {
              challengeId = parseInt(challengeMatch[1])
              fromChallengePage = true
              console.log(`[SIGNUP DEBUG] Extracted challengeId from current path: ${challengeId}`)
            }
          }
        }

        console.log(`[SIGNUP DEBUG] Referral signup - referralCode: ${referralCode}, competitionId: ${competitionId}, challengeId: ${challengeId}, currentPath: ${window.location.pathname}`)
        console.log(`[SIGNUP DEBUG] URL search params:`, urlParams.toString())
        console.log(`[SIGNUP DEBUG] Redirect URL:`, redirectUrl)

        const createResponse = await fetch('/api/auth/create-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userSession.access_token}`
          },
          body: JSON.stringify({
            id: supabaseUser.id,
            email: supabaseUser.email,
            firstName: supabaseUser.user_metadata?.firstName,
            lastName: supabaseUser.user_metadata?.lastName,
            username: supabaseUser.user_metadata?.username,
            referralCode: supabaseUser.user_metadata?.referralCode || referralCode,
            competitionId: supabaseUser.user_metadata?.competitionId || competitionId,
            challengeId: supabaseUser.user_metadata?.challengeId || challengeId,
            fromChallengePage: supabaseUser.user_metadata?.fromChallengePage || fromChallengePage,
            redirect: supabaseUser.user_metadata?.redirect
          })
        })

        if (createResponse.ok) {
          const userData = await createResponse.json()
          const newUser: AuthUser = {
            id: supabaseUser.id,
            email: supabaseUser.email!,
            firstName: userData.firstName,
            lastName: userData.lastName,
            username: userData.username,
            role: userData.role || 'user',
            profileImageUrl: userData.profileImageUrl,
            referralCode: userData.referralCode,
            idVerified: userData.idVerified || false,
            idVerifiedAt: userData.idVerifiedAt ? new Date(userData.idVerifiedAt) : null,
            phoneVerified: userData.phoneVerified || false,
            phoneVerifiedAt: userData.phoneVerifiedAt ? new Date(userData.phoneVerifiedAt) : null,
            phone: userData.phone || null,
            createdAt: userData.createdAt ? new Date(userData.createdAt) : null,
            supabaseUser
          }

          setUser(newUser)

          // Handle redirect for new users
          const redirectUrl = urlParams.get('redirect')
          const intent = urlParams.get('intent')
          const challengeId = urlParams.get('challengeId')

          console.log(`[NEW USER SUCCESS] New user created, redirect=${redirectUrl}, intent=${intent}, challengeId=${challengeId}`)

          // Check if this is a new Google OAuth user - they should complete their profile
          const isGoogleUser = supabaseUser.app_metadata?.provider === 'google'
          console.log(`[NEW USER PROFILE CHECK] Is Google user: ${isGoogleUser}`)

          if (isGoogleUser && !redirectUrl && intent !== 'join') {
            // Check if the new user actually needs profile completion
            const hasRequiredFields = userData.firstName && userData.lastName &&
              supabaseUser.user_metadata?.phone && supabaseUser.user_metadata?.dateOfBirth && supabaseUser.user_metadata?.gender

            console.log(`[NEW USER PROFILE CHECK] Has required fields: ${hasRequiredFields}`)

            if (!hasRequiredFields) {
              // Only redirect to profile completion if missing required fields
              console.log(`[NEW USER PROFILE CHECK] Redirecting new Google user to profile completion`)
              navigate('/progressive-profiling')
              return
            } else {
              console.log(`[NEW USER PROFILE CHECK] New Google user has complete profile, proceeding to dashboard`)
            }
          }

          // Navigate immediately without timeout to prevent 404 flash
          if (redirectUrl) {
            // Enhance redirect URL with auto-join markers for challenges
            let finalRedirectUrl = redirectUrl
            if (intent === 'join' && challengeId) {
              const redirectUrlObj = new URL(redirectUrl, window.location.origin)
              redirectUrlObj.searchParams.set('from', 'auth')
              redirectUrlObj.searchParams.set('intent', 'join')
              redirectUrlObj.searchParams.set('autoJoin', 'true')
              finalRedirectUrl = redirectUrlObj.pathname + redirectUrlObj.search
            }
            console.log(`[NEW USER SUCCESS] Navigating to ${finalRedirectUrl}`)
            navigate(finalRedirectUrl)
          } else if (intent === 'join' && challengeId) {
            // Handle challenge join when redirectUrl is null but intent and challengeId are present
            const challengeUrl = `/challenges/${challengeId}?from=auth&intent=join&autoJoin=true`
            console.log(`[NEW USER SUCCESS] Navigating to challenge with auto-join: ${challengeUrl}`)
            navigate(challengeUrl)
          } else if (newUser.role === 'admin') {
            console.log(`[NEW USER SUCCESS] Navigating to admin dashboard`)
            navigate('/admin')
          } else {
            console.log(`[NEW USER SUCCESS] Navigating to user dashboard`)
            navigate('/user')
          }
        } else {
          console.error('[USER CREATION] Failed to create user:', createResponse.status, await createResponse.text())
        }
      } else if (response.status === 403) {
        // Check if this is actually a deactivation (not just a generic 403)
        const errorData = await response.json().catch(() => ({ error: 'Access forbidden' }))
        const errorMessage = errorData.error || 'Access forbidden'
        
        // Only treat as deactivation if the message explicitly mentions deactivation
        const isDeactivated = errorMessage.toLowerCase().includes('deactivated') || 
                             errorMessage.toLowerCase().includes('misconduct')
        
        if (isDeactivated) {
          console.error('[FETCH USER DATA] Account deactivated:', errorMessage)

          // Sign out from Supabase to clear the session
          if (supabase) {
            await supabase.auth.signOut()
          }
          setUser(null)
          setSession(null)

          // Store error message for auth page to display
          sessionStorage.setItem('authErrorMessage', errorMessage)

          // Trigger a custom event to notify the auth page immediately
          window.dispatchEvent(new CustomEvent('authDeactivated', {
            detail: { message: errorMessage }
          }))

          // Redirect to auth page if not already there
          const currentPath = window.location.pathname
          if (currentPath !== '/auth' && currentPath !== '/signup' && currentPath !== '/signin') {
            navigate('/auth')
          }
        } else {
          // Generic 403 error (like after logout) - just log it
          console.log('[FETCH USER DATA] 403 Forbidden (not deactivated):', errorMessage)
        }

        setLoading(false)

        // Don't throw error - we've handled it
        return
      } else {
        console.error('[FETCH USER DATA] Failed to fetch user data:', response.status, await response.text())
        // If we can't fetch user data, still set loading to false to prevent infinite loading
        setLoading(false)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email: string, password: string, metadata?: { firstName?: string; lastName?: string; phone?: string }) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    // Get current URL parameters to preserve referral context
    const urlParams = new URLSearchParams(window.location.search)
    const referralCode = urlParams.get('ref')
    const redirectUrl = urlParams.get('redirect')

    // Build confirmation redirect URL that preserves context
    let confirmationRedirectTo = `${window.location.origin}/auth?type=signup`
    if (referralCode || redirectUrl) {
      const params = new URLSearchParams()
      params.set('type', 'signup')
      if (referralCode) params.set('ref', referralCode)
      if (redirectUrl) params.set('redirect', redirectUrl)
      confirmationRedirectTo = `${window.location.origin}/auth?${params.toString()}`
    }

    // Extract challenge information if signing up from challenge page or auth redirect
    let challengeId = null
    let fromChallengePage = false

    // First check URL parameters (for auth page redirects) - reuse existing urlParams
    const challengeIdParam = urlParams.get('challengeId')
    const intent = urlParams.get('intent')

    if (intent === 'join' && challengeIdParam) {
      challengeId = parseInt(challengeIdParam)
      fromChallengePage = true
      console.log(`[SIGNUP] Detected challenge signup from auth redirect: challengeId=${challengeId}`)
    } else {
      // Check current path (for direct signups on challenge pages)
      const currentPath = window.location.pathname
      if (currentPath.includes('/challenges/')) {
        const challengeMatch = currentPath.match(/\/challenges\/(\d+)/)
        if (challengeMatch) {
          challengeId = parseInt(challengeMatch[1])
          fromChallengePage = true
          console.log(`[SIGNUP] Detected challenge signup from challenge page: challengeId=${challengeId}`)
        }
      }
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          ...metadata,
          phone: metadata?.phone, // Explicitly include phone for backend validation
          referralCode,
          challengeId,
          fromChallengePage,
          redirect: redirectUrl
        },
        emailRedirectTo: confirmationRedirectTo
      }
    })

    // Check if user was automatically signed in (no email verification required)
    if (data?.user && data?.session) {
      // User is automatically signed in
      setSession(data.session)
      await fetchUserData(data.user, data.session)
      return { error: null }
    }

    // If there's a user but no session, email verification is required
    if (data?.user && !data?.session) {
      console.log('[SIGNUP] Email verification required for user:', data.user.email)
      return {
        error: {
          message: 'Registration successful! Please check your email to verify your account before signing in. The verification email may take 2-5 minutes to arrive. Check your spam folder if needed.'
        }
      }
    }

    return { error }
  }

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        return { error }
      }

      // If sign-in is successful, the auth state change listener will handle the rest
      // But we can also manually trigger it to ensure proper state update
      if (data?.session && data?.user) {
        setSession(data.session)
        await fetchUserData(data.user, data.session)
      }

      return { error: null }
    } catch (err: any) {
      // Catch errors from fetchUserData (like deactivated account)
      return { error: { message: err.message || 'An error occurred during sign in' } }
    }
  }

  const signInWithMagicLink = async (email: string) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth?type=magiclink`
      }
    })

    return { error }
  }

  const signInWithGoogle = async () => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    console.log('[GOOGLE AUTH] Initiating Google OAuth sign-in...')

    // Read referral parameters from sessionStorage (stored before OAuth redirect)
    const paramsToPreserve = ['ref', 'challengeId', 'social', 'comp', 'intent', 'redirect', 'ad_id', 'campaign_id', 'autoJoinChallenge', 'mobile'];
    const preservedParams = new URLSearchParams();
    preservedParams.set('type', 'oauth'); // Always include the auth type
    
    paramsToPreserve.forEach(param => {
      const value = sessionStorage.getItem(`google_oauth_${param}`);
      if (value) {
        preservedParams.set(param, value);
      }
    });

    const redirectUrl = `${window.location.origin}/auth?${preservedParams.toString()}`;
    console.log('[GOOGLE AUTH] Redirect URL with preserved params:', redirectUrl);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    })

    if (error) {
      console.error('[GOOGLE AUTH] OAuth initiation failed:', error)
    } else {
      console.log('[GOOGLE AUTH] OAuth initiated successfully, redirecting to Google...')
    }

    return { error }
  }

  // Add Facebook sign-in function
  const signInWithFacebook = async () => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    console.log('[FACEBOOK AUTH] Initiating Facebook OAuth sign-in...')

    // Read referral parameters from sessionStorage (stored before OAuth redirect)
    const paramsToPreserve = ['ref', 'challengeId', 'social', 'comp', 'intent', 'redirect', 'ad_id', 'campaign_id', 'autoJoinChallenge'];
    const preservedParams = new URLSearchParams();
    preservedParams.set('type', 'oauth'); // Always include the auth type
    
    paramsToPreserve.forEach(param => {
      const value = sessionStorage.getItem(`facebook_oauth_${param}`);
      if (value) {
        preservedParams.set(param, value);
      }
    });

    const redirectUrl = `${window.location.origin}/auth?${preservedParams.toString()}`;
    console.log('[FACEBOOK AUTH] Redirect URL with preserved params:', redirectUrl);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          scope: 'email'
        }
      }
    })

    if (error) {
      console.error('[FACEBOOK AUTH] OAuth initiation failed:', error)
    } else {
      console.log('[FACEBOOK AUTH] OAuth initiated successfully, redirecting to Facebook...')
    }

    return { error }
  }


  const signOut = async () => {
    // Sign out from Supabase first if configured
    if (supabase) {
      await supabase.auth.signOut()
    }

    // Clear any stored session data
    localStorage.removeItem('supabase.auth.token')
    localStorage.removeItem('sb-auth-token')
    
    // Clear any stored deactivation error messages
    sessionStorage.removeItem('authErrorMessage')

    // Clear user state
    setUser(null)
    setSession(null)

    // Use smooth client-side navigation to prevent 404 flash
    navigate('/')
  }

  const redirectToDashboard = () => {
    if (user?.role === 'admin') {
      navigate('/admin')
    } else {
      navigate('/user')
    }
  }

  const resetPassword = async (email: string) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return { error }
  }

  const updatePassword = async (newPassword: string) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured. Please contact support.' } }
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    return { error }
  }

  const value = {
    user,
    session,
    loading: loading || initialLoad,
    signUp,
    signIn,
    signInWithMagicLink,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    redirectToDashboard,
    resetPassword,
    updatePassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
